import React, {useEffect, useMemo, useState} from 'react';
import {
    useActiveProjectCalendarsSelector,
    useChecklistMapSelector,
    useCpmMapSelector
} from "../../../../store/selectors/project.selectors";
import {
    useAllTaskListSelector,
    useCompletedChecklistMapSelector, useFilteredTaskListSelector, useTaskCodeMapSelector
} from "../../../../store/selectors/task/task.selectors";
import TaskStatusModel from "../../../../models/responses/task-status.model";
import {convertIndexToSeconds} from "../../../../utils/cpm-functions/cpm-app/functions/handleEvent";
import {useSearchParamatersSelector} from "../../../../store/selectors/search.selectors";
import styles from "./Checklist.module.scss";
import CheckListItems from "./components/CheckListItems";
import {useWorkPackagesSelector, useWorkStreamsSelector} from "../../../../store/selectors/work-packages.selectors";
import {WorkPackage, WorkStream} from "../../../../models/responses/work-package.model";
import TaskModel from "../../../../models/responses/task.model";
import {CpmTaskModel} from "../../../../models/responses/cpm-task.model";
import moment from "moment/moment";
import checklistFilteringUtils from "../../../../utils/checklist-filtering.utils";
import filterChecklistItems from "../../../../utils/checklist-filtering.utils";

export default function CheckListPage() {
    const allTasks = useAllTaskListSelector();
    const cpmMap = useCpmMapSelector();
    const calendarMap = useActiveProjectCalendarsSelector();
    const checklistMap = useChecklistMapSelector();
    const searchParams = useSearchParamatersSelector();
    const completedChecklistMap = useCompletedChecklistMapSelector();
    const [dueItems, setDueItems] = useState<any>([]);
    const [plannedItems, setPlannedItems] = useState<any>([]);
    const [completedItems, setCompletedItems] = useState<any>([]);
    const workPackages = useWorkPackagesSelector();
    const workStreams = useWorkStreamsSelector();
    const taskCodeMap = useTaskCodeMapSelector();
    const filteredTaskList = useFilteredTaskListSelector();

    const workStreamMap = useMemo(() =>
        workStreams.reduce((acc, workStream) => {
            acc.set(workStream.workStreamId, workStream);
            return acc;
        }, new Map<string, WorkStream>()),
        [workStreams]);

    const checklistItemsData = useMemo(() => {
        let incompleteChecklistItems: any = [];
        filteredTaskList.forEach(task => {
            if (task.status === TaskStatusModel.COMPLETE || task.status === TaskStatusModel.DECLARED_COMPLETE) return;
            if (!task.checklistId) return;
            const cpmTask = cpmMap.get(task.task_id);
            const checklist = checklistMap.get(task.checklistId);
            if (!cpmTask || !checklist) return;
            const workingHours = calendarMap.get(`whd:${cpmTask.cal_id}`)
            checklist.items.forEach((checklistItem: any) => incompleteChecklistItems.push({
                ...checklistItem,
                taskId: task.task_id,
                taskString: task.wbsPath ? `${task.wbsPath} / ${task.task_name} (${task.task_code})` :
                    `${task.task_name} (${task.task_code})`,
                due: checklistItem.tMinusDays > 0 ?
                    convertIndexToSeconds((cpmTask.ff ? cpmTask.ff : cpmTask.ef) + (checklistItem.tMinusDays * workingHours * 2), cpmTask.cal_id, calendarMap) :
                    convertIndexToSeconds((cpmTask.fs ? cpmTask.fs : cpmTask.es) + (checklistItem.tMinusDays * workingHours * 2), cpmTask.cal_id, calendarMap),
                completionRecord: completedChecklistMap.get(`${task.task_id}:${checklistItem.checklistItemId}`),
                type: 'task',
            }));
        })

        workPackages.forEach((wp: WorkPackage) => {
            const tasks: TaskModel[] = wp.assignedTaskCodes
                .map(taskCode => allTasks.get(taskCodeMap.get(taskCode)!))
                .filter((task): task is TaskModel => task !== undefined)
            let packageStart: null | number = null;
            let packageEnd: null | number = null;
            let isCritical = false;
            let isCriticalNow = false;
            let isNearCritical = false;
            let isInProgress = false;
            let drivingTaskStart: null | TaskModel = null;
            let drivingTaskEnd: null | TaskModel = null;
            tasks.forEach(task => {
                const cpmTask: CpmTaskModel = cpmMap.get(task.task_id);
                if (!cpmTask) return;
                const taskStart = convertIndexToSeconds(cpmTask.fs ? cpmTask.fs : cpmTask.es, cpmTask.cal_id, calendarMap);
                const taskEnd = convertIndexToSeconds(cpmTask.ff ? cpmTask.ff : cpmTask.ef, cpmTask.cal_id, calendarMap);
                const workingHours = calendarMap.get(`whd:${cpmTask.cal_id}`);
                if (packageStart === null || taskStart < packageStart) {
                    packageStart = taskStart;
                    drivingTaskStart = task;
                }
                if (packageEnd === null || taskEnd > packageEnd) {
                    packageEnd = taskEnd;
                    drivingTaskEnd = task;
                }
                if (cpmTask.float === 0 || (cpmTask.float && cpmTask.float < workingHours)) {
                    isCritical = true;
                    if (convertIndexToSeconds(cpmTask.ls, cpmTask.cal_id, calendarMap) <=
                        (moment(new Date()).add(1, 'days').toDate().getTime() / 1000)) {
                        isCriticalNow = true;
                    }
                } else if (cpmTask.float && cpmTask.float < workingHours * 2) {
                    isNearCritical = true;
                }
                if (task.status !== TaskStatusModel.NOT_STARTED && task.status !== TaskStatusModel.BLOCK) {
                    isInProgress = true;
                }
            });

            if (!wp.checklistId) return;
            const checklist = checklistMap.get(wp.checklistId);
            if (!checklist) return;
            let earliestDueDate: null | number = null;
            let overdueItemsCount = 0;
            const workStream = workStreamMap.get(wp.workStreamId);
            checklist.items.forEach((checklistItem: any) => {
                const completionRecord = completedChecklistMap.get(`${wp.workPackageId}:${checklistItem.checklistItemId}`);
                const cpmTask = drivingTaskStart && drivingTaskEnd ?
                    checklistItem.tMinusDays <= 0 ? cpmMap.get(drivingTaskStart.task_id) : cpmMap.get(drivingTaskEnd.task_id) : null;
                if (!cpmTask) return;
                const workingHours = cpmTask ? calendarMap.get(`whd:${cpmTask.cal_id}`) : 8;
                const dueDate = checklistItem.tMinusDays > 0 ?
                    convertIndexToSeconds((cpmTask.ff ? cpmTask.ff : cpmTask.ef) + (checklistItem.tMinusDays * workingHours * 2), cpmTask.cal_id, calendarMap) :
                    convertIndexToSeconds((cpmTask.fs ? cpmTask.fs : cpmTask.es) + (checklistItem.tMinusDays * workingHours * 2), cpmTask.cal_id, calendarMap)
                if (!completionRecord && dueDate && dueDate < (new Date().getTime() / 1000)) {
                    overdueItemsCount += 1;
                }
                if (earliestDueDate === null || dueDate < earliestDueDate) {
                    earliestDueDate = dueDate;
                }
                incompleteChecklistItems.push({
                    ...checklistItem,
                    type: 'workPackage',
                    due: dueDate,
                    taskId: wp.workPackageId,
                    taskString: `Work Package: ${workStream?.workStreamName || ''} / ${wp.workPackageName} (${wp.workPackageRef})`,
                    completionRecord: completionRecord,
                    workPackage: wp,
                })
            });
        });

        return incompleteChecklistItems.sort((a, b) => a.due - b.due);
    }, [allTasks, searchParams, workPackages])

    useEffect(() => {
        if (!checklistItemsData.length) {
            setDueItems([]);
            setPlannedItems([]);
            return
        }
        let dueOut: any = [];
        let plannedOut: any = [];
        let completedItems: any = [];
        let index = 0;
        checklistItemsData.forEach((item: any) => {
            if (!filterChecklistItems(item, searchParams, item.workPackage, allTasks.get(item.taskId))) {
                return;
            }
            if (item.completionRecord) {
                if (item.completionRecord.timestamp.seconds > new Date().getTime() / 1000 - 604800) {
                    completedItems.push(item);
                }
            } else if (item.due * 1000 <= new Date().getTime()) {
                dueOut.push({...item, index: index + 1});
                index++;
            } else {
                plannedOut.push({...item, index: index + 1});
                index++;
            }
        })
        setDueItems(dueOut);
        setPlannedItems(plannedOut);
        setCompletedItems(completedItems.sort((a, b) =>
            b.completionRecord.timestamp.seconds - a.completionRecord.timestamp.seconds));
    }, [checklistItemsData]);

    return (
        <div style={{minWidth: "100%", maxWidth: "100%", overflow: "hidden"}}>
            <div className={styles.ChecklistHeaders}>
                <div className={styles.ChecklistHeadersLeft}>
                    <div className={styles.ChecklistHeader}>Priority</div>
                </div>
                <div className={styles.ChecklistHeadersRight}>
                    <div className={styles.ChecklistHeader}>Due Date</div>
                    <div className={styles.ChecklistHeaderRightAligned}>Deadline</div>
                </div>
            </div>
            <div
                id="cl-container"
                className={styles.ChecklistSectionContainer}
                style={{
                    maxHeight: `calc(100vh - 200px)`,
                    minWidth: "100%",
                    overflowX: "hidden",
                    overflowY: "auto"
            }}
            >
                <CheckListItems
                    checkListItemList={completedItems}
                    title="Completed (last 7 days)"
                />
                <CheckListItems
                    checkListItemList={dueItems}
                    title="Due"
                />
                <CheckListItems
                    checkListItemList={plannedItems}
                    title="Planned"
                />
            </div>
        </div>
    )
}